import { useRef, useMemo, useEffect, useState, useContext } from 'react'
import { useParams } from 'react-router'
import { useResizeObserver } from './util/useSize'
import { Canvas, useFrame, useLoader } from '@react-three/fiber'
import { DirectionalLight, Mesh, Object3D, Raycaster, Vector3, Vector4 } from 'three'
import { degToRad, lerp, randFloat, randInt } from 'three/src/math/MathUtils'
import { calculateTimeToCrossThreshold, feetToM, findIntersection, getBallPositionDuringPitch, getBallXYSpeed, inchesToM, makeSymmetricalArc, mphToMetersPerSecond } from './util/MathUtil'
import { GameFeed } from './data/MLBGameFeed'
import useCreateGameData, { CameraView, CameraViewEnum, GameFeedContext } from './useCreateGameData'
import useStatisProGameData, { StatisProGameContext } from './useStatisProGameData'
import EventNavList from './components/EventNavList'
import { useLocalStorage } from './util/useStorage'
import { AnimationGroup, AnimationGroupType, CancelledAnimationGroupError } from './util/Animation'
import { clamp, cloneDeep } from 'lodash'
import { OffensivePlayer, PlayerControls } from './components/Players'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { CONSTANTS, eventCodeToDisplayText, FIELD_LOCATION, getLocationForBaseCode, OffensePositionKeys, PITCH_ANIMATION_DATA } from './Constants'
import StrikeZone from './components/StrikeZone'
import { Pixelation, EffectComposer, Bloom, ChromaticAberration } from '@react-three/postprocessing'
import { TrailRenderer } from './util/TrailRenderer'
import useDebugControls from './useDebugControls'
import BallImpactLocation from './components/BallImpactLocation'
import styles from './css/LiveGameView.module.css'
import Crowd from './components/Crowd'
import { ScoreboardHTML } from './components/Scoreboard'
import ScoreCard from './components/ScoreCard'
import GameControlPanel from './components/GameControlPanel'
import ToneController from './util/ToneController'
import Park from './components/Park'
import { NavLink } from 'react-router-dom'
import BatHitSoundUrl from './sounds/fx BatHit.mp3'
import MitCatchSound from './sounds/fx MitCatch.mp3'
import ThrowSound from './sounds/fx Throw.mp3'
import HitDirt from './sounds/fx HitDirt.mp3'
import OverlayMenu from './components/OverlayMenu'

export type UILayout = 'BOTTOM' | 'LEFT'

function getInningText(inning: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd']
  const remainder = inning % 100
  if (remainder >= 11 && remainder <= 13) {
      return `${inning}th`
  }
  const suffixIndex = (inning + 1) % 10
  const suffix = suffixes[suffixIndex] || suffixes[0] // Default to "th" if none matches
  return `${inning + 1}${suffix}`
}

export default function LiveGameView() {
  const { gameId } = useParams()
  // Check if this is a Statis Pro simulation (gameId = 0) or MLB game
  const isStatisProGame = gameId === '0'
  const gamePk = isStatisProGame ? undefined : parseInt(gameId ?? '')
  const [statisProStore, statisProDispatch] = useStatisProGameData()
  const [mlbStore, mlbDispatch] = useCreateGameData(gamePk)

  // Choose which store and dispatch to use based on game type
  const [store, dispatch] = isStatisProGame
    ? [statisProStore, statisProDispatch]
    : [mlbStore, mlbDispatch]
  const [canvasContainerRef, setCanvasContainerRef] = useState<HTMLDivElement | null>(null)
  const canvasContainerSize = useResizeObserver(canvasContainerRef)
  const [useDebugView, setUseDebugView] = useState(false)
  const [showSceneTransition, setShowSceneTransition] = useState({ transitionActive: false, showInningText: false })
  const [layout, setLayout] = useLocalStorage<UILayout>('layout', 'BOTTOM')
  const [showMenu, setShowMenu] = useState(false)
  const canvasSize = useMemo(() => canvasContainerSize &&
    ({
      width: Math.min(canvasContainerSize.width, canvasContainerSize.height),
      height: Math.min(canvasContainerSize.width, canvasContainerSize.height)
    }),
    [canvasContainerSize]
  )

  const { currentPlay, playResultText, currentPlayHasCompleted, isMobileLayout, rawData, playIndex, eventIndex, freeCam, isHalfInningFinalEvent, latestGameLogEntry } = store

  const incrementEvent = useMemo(() => {
    let inProgress = false
    return (increment: number) => {
      if (inProgress || canvasContainerRef == null) return
      inProgress = true

      setShowSceneTransition({ showInningText: false, transitionActive: true })
      setTimeout(() => {
        inProgress = false
        dispatch({ type: 'INCREMENT_EVENT', increment })

        // Regular event transition
        if (!isHalfInningFinalEvent) {
          setShowSceneTransition({ showInningText: false, transitionActive: false })

        // Inning transition
        } else if (currentPlay) {
          setShowSceneTransition({ showInningText: true, transitionActive: true })
          setTimeout(() => {
            setShowSceneTransition({ showInningText: false, transitionActive: false })
          }, 1400)
        }
      }, 500)
    }
  }, [canvasContainerRef, dispatch, isHalfInningFinalEvent, currentPlay])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft': return incrementEvent(-1)
        case 'ArrowRight': return incrementEvent(1)
        case '1': return 
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [canvasContainerRef, dispatch, eventIndex, gameId, playIndex, setLayout, incrementEvent])

  function handleScoreboardTextureLoaded(image: HTMLImageElement) {
    dispatch({ type: 'SET_SCOREBOARD_IMAGE', image })
  }
  if (currentPlay == null) {
    // For Statis Pro games, show loading state while simulation initializes
    if (isStatisProGame) {
      return (
        <div className={styles.absoluteCentered} style={{ textAlign: 'center' }}>
          Loading Statis Pro simulation...
          <NavLink to={"/games"} style={{ display: 'block' }}>Back to menu</NavLink>
        </div>
      )
    }
    
    // For MLB games, check if scheduled
    // if (rawData?.gameData.status.detailedState === 'Scheduled') {
    //   return (
    //     <div className={styles.absoluteCentered} style={{ textAlign: 'center' }}>
    //       Game not yet started
    //       <NavLink to={"/games"} style={{ display: 'block' }}>Back to menu</NavLink>
    //     </div>
    //   )
    // }
    return null
  }

  // Use the appropriate context provider based on game type
  const ContextProvider = isStatisProGame ? StatisProGameContext.Provider : GameFeedContext.Provider
  return (
    <ContextProvider value={[store, dispatch] as any}>
      <div className="App">
        <div className={styles.menuButton} onClick={() => setShowMenu(true)}>MENU</div>
        {showMenu && <OverlayMenu onClose={() => setShowMenu(false)} />}
        <div style={{ position: 'absolute', left: '0', width: 0, height: 0, overflow: 'hidden' }}>
          {(
            <ScoreboardHTML onComplete={handleScoreboardTextureLoaded} />
          )}
        </div>
        <GameControlPanel incrementEvent={incrementEvent} />
        <div className={layout === 'BOTTOM' ? styles.flexCol : styles.flexRow} style={{ height: '100%', position: 'relative' }}>
          <div className={styles.playResultText} style={{ opacity: showSceneTransition.showInningText ? 1 : 0, zIndex: 1 }}>{`${currentPlay.about.halfInning.toUpperCase()} OF THE ${getInningText(currentPlay.about.inning - 1)}`}</div>
          <div className={styles.canvasContainer} ref={setCanvasContainerRef} style={{ height: layout === 'BOTTOM' ? 650 : '', filter: showSceneTransition.transitionActive ? 'brightness(0)' : 'brightness(1)' }}>
            {freeCam && <div onClick={() => dispatch({ type: 'SET_FREE_CAM', freeCam: false })} className={styles.freeCamButton}>EXIT FREE CAM</div>}
            {playResultText && <div className={styles.playResultText}>{playResultText}</div>}
            {currentPlayHasCompleted && (
              <div style={{ zIndex: 1, position: 'absolute', top: 0, margin: 15, right: 0, left: 0, display: 'flex', justifyContent: 'center' }}>
                <div style={{ backgroundColor: 'rgb(0, 0, 0, .8)', padding: 8 }}>
                  {/* Use more user-friendly description from Statis Pro game log if available */}
                  {isStatisProGame && latestGameLogEntry?.description
                    ? latestGameLogEntry.outcome
                    : currentPlay.result.description}
                </div>
              </div>
            )}
            {canvasSize && (
              <Canvas
                shadows="percentage"
                camera={{ position: [0, 50.7, -70.7], rotation: [-2.7, 0, -3.15], near: 0.01, far: 750, fov: 25 }}
                dpr={devicePixelRatio * 0.4}
              >
                <EffectComposer>
                  <Bloom luminanceThreshold={0.5} luminanceSmoothing={0.9} height={300} />
                  <ChromaticAberration radialModulation={false} modulationOffset={1} opacity={1} />
                  <Pixelation granularity={2} />
                </EffectComposer>
                <Canvas3dView incrementEvent={incrementEvent} isStatisProGame={isStatisProGame} />
              </Canvas>
            )}
          </div>
          {useDebugView && <EventNavList layout={layout} />}
          {<ScoreCard />}
        </div>
      </div>
    </ContextProvider>
  )
}

const BASE_ORDER: (GameFeed.BaseKey | null)[] = [
  null, '1B', '2B', '3B', 'score'
]

function getBaseIndexForMovementKey(key: GameFeed.BaseKey | null | '4B') {
  console.log("Key: " + key)
  switch (key) {
    case null: return 0
    case '1B': return 1
    case '2B': return 2
    case '3B': return 3
    case 'score':
    case '4B': return 4
    default: return -1
  }
}

function playerAnimateToLocation({ runner, locations, playSpeed, duration, lookAt, animGroup }: {
  runner: PlayerControls,
  locations: Vector3[],
  playSpeed: number,
  duration: number,
  lookAt?: Vector3 | Object3D,
  animGroup: AnimationGroupType
}) {
  runner.setAnimationState('Running')
  let finished = false
  return locations.reduce<Promise<void>>((lastPromise, newLoc, i) =>
    i === 0 ? lastPromise : lastPromise.then(() => animGroup.animate(elapsedTime => {
      const prevLoc = locations[i - 1]
      const location = prevLoc.clone().lerp(newLoc, (elapsedTime / duration))
      const lookTarget = lookAt instanceof Vector3 ? lookAt : newLoc

      const distance = lookTarget.distanceTo(location)
        // TODO cleanup
      if (i === locations.length - 1 && (duration - elapsedTime) < 500 && !finished && runner.type === 'offense') {
        finished = true
        runner.setAnimationState('SlideStop')
      }
      runner.scene.position.set(location.x, location.y, location.z)
      if (!finished && distance > 1) {
        runner.scene.lookAt(lookTarget.x, 0, lookTarget.z)
      }
    }, { duration, playSpeed }))
    , Promise.resolve())
    .then(() => {
      runner.setAnimationState('StandingNeutral')
    })
}

const animateBaserun = ({ runner, movement, playSpeed, animGroup }: { runner: PlayerControls, movement: GameFeed.Movement, playSpeed: number, animGroup: AnimationGroupType }) => {
  const startIndex = getBaseIndexForMovementKey(movement.start)
  // prefer explicit end; fall back to outBase when present
  const endIndex = getBaseIndexForMovementKey(movement.end ?? movement.outBase)
  
  // base location not found, short circuit with diagnostics
  if (startIndex === -1 || endIndex === -1) {
    console.warn('[3D ENGINE] Unmapped base key(s) for movement', {
      start: movement.start,
      end: movement.end,
      outBase: movement.outBase
    })
    return Promise.resolve()
  }
  
  // FIX for Problem #2: For outs, only animate partway to show realistic out timing
  let locations = BASE_ORDER.slice(startIndex, endIndex + 1).map(base => getLocationForBaseCode(base, 'offense'))
  
  if (movement.isOut && movement.outBase && !movement.end) {
    // For outs, animate only partway to the out base to show realistic timing
    const startPos = locations[0]
    const outBasePos = locations[locations.length - 1]
    
    // Create a position 60-80% of the way to the out base (realistic for most ground outs)
    const outPercentage = 0.6 + (Math.random() * 0.2) // 60-80% of the way
    const partialOutPos = startPos.clone().lerp(outBasePos, outPercentage)
    
    // Only animate to the start position and the partial out position
    locations = [startPos, partialOutPos]
    
    console.log('[DEBUG] FIXED - Out animation will stop partway:', {
      playerPosition: runner.playerPos,
      isOut: movement.isOut,
      outBase: movement.outBase,
      outPercentage: `${(outPercentage * 100).toFixed(1)}%`,
      originalPath: `${BASE_ORDER[startIndex]} -> ${BASE_ORDER[endIndex]}`,
      newPath: `${BASE_ORDER[startIndex]} -> ${outPercentage * 100}% to ${BASE_ORDER[endIndex]}`
    })
  } else {
    console.log('[DEBUG] Normal baserunning animation:', {
      playerPosition: runner.playerPos,
      isOut: movement.isOut,
      path: BASE_ORDER.slice(startIndex, endIndex + 1).join(' -> ')
    })
  }

  return animGroup.delay(runner.playerPos === 'B' ? 300 : 0, { playSpeed })
  .then(() =>
    playerAnimateToLocation({ runner, locations, playSpeed, duration: 2900 + ((Math.random() * 400) - 200) , animGroup })
  )
}

const batHitSound = new Audio(BatHitSoundUrl)
const mitCatchSound = new Audio(MitCatchSound)
const throwSound = new Audio(ThrowSound)
const hitDirtSound = new Audio(HitDirt)

throwSound.volume = 0.2

function playSound(audio: HTMLAudioElement) {
  return audio.play().catch(e => {})
}

function Canvas3dView({ incrementEvent, isStatisProGame }: { incrementEvent: (inc: number) => void, isStatisProGame: boolean }) {
  const { scene: parkScene } = useLoader(GLTFLoader, `${process.env.PUBLIC_URL}/assets/oracle-park.glb`)
  const playerGLTF = useLoader(GLTFLoader, `${process.env.PUBLIC_URL}/assets/player.glb`)
  const { scene: baseball } = useLoader(GLTFLoader, `${process.env.PUBLIC_URL}/assets/ball.glb`)
  const lightRef = useRef<DirectionalLight>(null)
  const [showBaseball, setShowBaseball] = useState(false)
  const lightTargetRef = useRef<Object3D>(null)
  const [strikeZonePosition, setStrikeZonePosition] = useState<[number, number, number]>()
  
  // Use the appropriate context based on game type
  const mlbContext = useContext(GameFeedContext)
  const statisProContext = useContext(StatisProGameContext)
  const [{ isFirstEventOfInning, muteEnabled, autoPlayEnabled = false, isFinalEvent, currrentRunnersOnBase, linescore, currentEvent, currentPlay, ballLandingLocation, playSpeed, eventIndex, prevPlay }, dispatch] = isStatisProGame ? statisProContext : mlbContext
  const [runnersStatus, setRunnersStatus] = useState<{ [key in OffensePositionKeys]?: GameFeed.Runner }>({})
  const [ballDidHitGround, setBallDidGround] = useState(false)
  const isTopInning = currentPlay?.about.isTopInning ?? false
  const tone = useMemo(() => new ToneController(), [])

  useEffect(() => {
    tone.toggleMute(muteEnabled)

    batHitSound.muted = muteEnabled
    mitCatchSound.muted = muteEnabled
    throwSound.muted = muteEnabled
    hitDirtSound.muted = muteEnabled
  }, [muteEnabled, tone])
  
  const offense = useMemo(() => ({
    '1B': new PlayerControls(playerGLTF, 'offense', '1B', isTopInning ? 'away' : 'home'),
    '2B': new PlayerControls(playerGLTF, 'offense', '2B', isTopInning ? 'away' : 'home'),
    '3B': new PlayerControls(playerGLTF, 'offense', '3B', isTopInning ? 'away' : 'home'),
    'B': new PlayerControls(playerGLTF, 'offense', 'B', isTopInning ? 'away' : 'home'),
  }) as { [key in OffensePositionKeys]: PlayerControls }, [isTopInning, playerGLTF])

  const defense = useMemo(() => ({
    '1B': new PlayerControls(playerGLTF, 'defense', '1B', isTopInning ? 'home' : 'away'),
    '2B': new PlayerControls(playerGLTF, 'defense', '2B', isTopInning ? 'home' : 'away'),
    '3B': new PlayerControls(playerGLTF, 'defense', '3B', isTopInning ? 'home' : 'away'),
    'SS': new PlayerControls(playerGLTF, 'defense', 'SS', isTopInning ? 'home' : 'away'),
    'LF': new PlayerControls(playerGLTF, 'defense', 'LF', isTopInning ? 'home' : 'away'),
    'CF': new PlayerControls(playerGLTF, 'defense', 'CF', isTopInning ? 'home' : 'away'),
    'RF': new PlayerControls(playerGLTF, 'defense', 'RF', isTopInning ? 'home' : 'away'),
    'P': new PlayerControls(playerGLTF, 'defense',  'P', isTopInning ? 'home' : 'away'),
    'C': new PlayerControls(playerGLTF, 'defense',  'C', isTopInning ? 'home' : 'away'),
  }) as { [key in GameFeed.DPosAbbr]: PlayerControls }, [playerGLTF, isTopInning])

  const [intersectPointDebug, setIntersectPointDebug] = useState<Vector3>()

  const trail = useMemo(() => new TrailRenderer( parkScene, true ), [parkScene])
  useEffect(() => {
    // specify points to create planar trail-head geometry
    const trailHeadGeometry = [
      new Vector3( -.05, 0.0, 0.0 ), 
      new Vector3( 0.0, .05, 0.0 ),
      new Vector3( .05, 0.0, 0.0 ),
      new Vector3( 0.0, -.05, 0.0 ),
      new Vector3( -.05, 0.0, 0.0 ), 
    ]
    const trailMaterial = TrailRenderer.createBaseMaterial()
    trailMaterial.uniforms.headColor = { value: new Vector4(1, 1, 1, 0.2) }
    trailMaterial.uniforms.tailColor = { value: new Vector4(1, 1, 1, 0.0) }

    const trailLength = 40
    trail.initialize( trailMaterial, trailLength, false, 0, trailHeadGeometry, baseball)

    return () => {
      trail.destroyMesh()
      trail.deactivate()
      trail.removeFromParent()
    }
  }, [baseball, parkScene, trail])

  useEffect(() => {
    trail.setAdvanceFrequency(playSpeed * 60)
  }, [playSpeed, trail])

  useFrame(() => {
    trail.update()
    trail.geometry?.computeBoundingSphere()
  })

  useFrame((_, deltaSeconds) => {
    // lol
    for (const key in offense) {
      // @ts-ignore
      offense[key].update(deltaSeconds)
    }
    for (const key in defense) {
      // @ts-ignore
      defense[key].update(deltaSeconds)
    }
  })

  useEffect(() => {
    parkScene.castShadow = true
    parkScene.receiveShadow = true
    parkScene.traverse((child) => {
      if (child instanceof Mesh) {
        child.castShadow = true
        child.receiveShadow = true
      }
    })

    Object.values(GameFeed.DPosAbbrEnum).forEach(abbr => {
      if (abbr === 'P' && currentEvent?.pitchData) {
        const handReleaseLocation = currentPlay?.matchup.pitchHand.code === 'R'
          ? PITCH_ANIMATION_DATA.BALL_RIGHT_HAND_RELEASE_POSITION
          : PITCH_ANIMATION_DATA.BALL_LEFT_HAND_RELEASE_POSITION
        const rightHandThrowLocation = new Vector3(...handReleaseLocation)
        const newPosition = new Vector3(-currentEvent.pitchData.coordinates.x0, currentEvent.pitchData.coordinates.z0, currentEvent.pitchData.coordinates.y0).multiplyScalar(.3048)
        const pitcherOrigin = newPosition.add(rightHandThrowLocation)

        defense[abbr].setGloveHand(currentPlay?.matchup.pitchHand.code === 'R' ? 'L' : 'R')
        defense[abbr].scene.position.set(pitcherOrigin.x, pitcherOrigin.y, pitcherOrigin.z)
      } else {
        defense[abbr].setGloveHand('L')
        defense[abbr].scene.position.set(FIELD_LOCATION.DEFENSE[abbr].x, FIELD_LOCATION.DEFENSE[abbr].y, FIELD_LOCATION.DEFENSE[abbr].z)
      }
      defense[abbr].scene.lookAt(FIELD_LOCATION.BASE.HOME)

      if (abbr === 'C') {
        defense[abbr].setAnimationState('CatcherIdle')
      } else {
        defense[abbr].setAnimationState('StandingNeutral')
      }
      
      // Ensure defensive players are visible and properly positioned
      defense[abbr].scene.visible = true
    })

    offense['B'].scene.setRotationFromAxisAngle(new Vector3(0, 1, 0), degToRad(-90))
    offense['B'].scene.position.set(FIELD_LOCATION.OFFENSE['B'].x, FIELD_LOCATION.OFFENSE['B'].y, FIELD_LOCATION.OFFENSE['B'].z)
    offense['B']?.setAnimationState('BattingIdle')
    offense['B']?.setGloveHand(undefined)

    // DEBUG: Log runner positioning to understand the duplicate player issue
    console.log('[DEBUG] Setting up base runners:', {
      currentRunnersOnBase: currrentRunnersOnBase,
      eventIndex,
      playIndex: currentPlay?.atBatIndex,
      isStatisProGame,
      currentBatter: currrentRunnersOnBase['B']?.fullName,
      firstBase: currrentRunnersOnBase['1B']?.fullName,
      secondBase: currrentRunnersOnBase['2B']?.fullName,
      thirdBase: currrentRunnersOnBase['3B']?.fullName
    })

    if (currrentRunnersOnBase['1B']) {
      const position = getLocationForBaseCode('1B', 'offense')
      offense['1B'].scene.position.set(position.x, position.y, position.z)
      offense['1B'].scene.lookAt(FIELD_LOCATION.BASE.HOME)
      offense['1B']?.setAnimationState('RunnerAtBaseIdle')
      offense['1B']?.setGloveHand(undefined)
      console.log('[DEBUG] Positioned 1B runner:', currrentRunnersOnBase['1B'].fullName)
    }
    if (currrentRunnersOnBase['2B']) {
      const position = getLocationForBaseCode('2B', 'offense')
      offense['2B'].scene.position.set(position.x, position.y, position.z)
      offense['2B'].scene.lookAt(FIELD_LOCATION.BASE.HOME)
      offense['2B']?.setAnimationState('RunnerAtBaseIdle')
      offense['2B']?.setGloveHand(undefined)
      console.log('[DEBUG] Positioned 2B runner:', currrentRunnersOnBase['2B'].fullName)
    }
    if (currrentRunnersOnBase['3B']) {
      const position = getLocationForBaseCode('3B', 'offense')
      offense['3B'].scene.position.set(position.x, position.y, position.z)
      offense['3B'].scene.lookAt(FIELD_LOCATION.BASE.HOME)
      offense['3B']?.setAnimationState('RunnerAtBaseIdle')
      offense['3B']?.setGloveHand(undefined)
      console.log('[DEBUG] Positioned 3B runner:', currrentRunnersOnBase['3B'].fullName)
    }
      setIntersectPointDebug(undefined)
  }, [defense, currentEvent, offense, prevPlay, currrentRunnersOnBase, parkScene, currentPlay])

  const customPlayInfo = useMemo(() => currentPlay && ({
    wasCaught: (['Pop Out', 'Flyout', 'Lineout'] as GameFeed.ResultEvent[]).includes(currentPlay.result.event)
  }), [currentPlay])

  const animationRefs = useRef({ autoPlayEnabled })
  animationRefs.current = { autoPlayEnabled }

  // Track runner movements already animated during this event
  const animatedThisEventRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    animatedThisEventRef.current = new Set()
  }, [eventIndex])

  useEffect(() => {
    const pitchData = currentEvent?.pitchData
    const animGroup = AnimationGroup()
    const baseballMesh = baseball.getObjectByName('Sphere')

    if (currentEvent == null || currentPlay == null) return
    
    console.log('[3D ENGINE] Starting animation for:', {
      outcome: currentPlay?.result?.description,
      eventIndex,
      playIndex: currentPlay?.atBatIndex,
      isStatisProGame,
      hasCurrentEvent: !!currentEvent,
      hasCurrentPlay: !!currentPlay,
      hasPitchData: !!currentEvent?.pitchData
    })
    
    // Reset animation state for new play
    setShowBaseball(false)
    setStrikeZonePosition(undefined)
    setBallDidGround(false)
    trail.reset()
    trail.pause()
    
    async function initAnimations() {
      if (currentPlay == null || currentEvent == null) {
        console.log('[3D ENGINE] Skipping animation - missing play or event')
        return
      }
      

      if (eventIndex === 0) {
        dispatch({ type: 'SET_CAMERA_VIEW', cameraView: { type: CameraViewEnum.Scoreboard } })
        await animGroup.delay(isFirstEventOfInning ? 4000 : 2500)
      }

      if (currentEvent.details.code === GameFeed.EventDetailsCode.PitcherStepOff) {
        dispatch({ type: 'SET_PLAY_RESULT_TEXT', text: 'PITCHER STEP OFF' })
      }
      if (currentEvent.type === 'pickoff') {
        dispatch({ type: 'SET_PLAY_RESULT_TEXT', text: 'PICKOFF ATTEMPT' })
      }
      
      if (currentEvent.type === 'action') {
        dispatch({ type: 'SET_PLAY_RESULT_TEXT', text: eventCodeToDisplayText(currentEvent) })
        dispatch({ type: 'SET_CAMERA_VIEW', cameraView: { type: CameraViewEnum.StaticBasesOverview }})
        // console.log(currentEvent) b
        if (currentEvent.details.eventType === 'pitching_substitution') {
        }
        if (
          currentEvent.details.eventType === 'defensive_indiff' ||
          currentEvent.details.eventType === 'stolen_base_3b' ||
          currentEvent.details.eventType === 'stolen_base_2b'
        ) {
          const baseStealingRunners = currentPlay?.runners.filter((runner: any) => runner.details.playIndex === currentEvent.index)
          await Promise.all(
            baseStealingRunners.map((runner: any) => {
              // @ts-ignore
              const fbx = offense[runner.movement.start ?? 'B']
              return animateBaserun({ runner: fbx, movement: runner.movement, playSpeed, animGroup })
            })
          )
        }
      }

      const ballWasNotHit = ([
        GameFeed.EventDetailsCode.Ball,
        GameFeed.EventDetailsCode.BallInDirt,
        GameFeed.EventDetailsCode.CalledStrike,
        GameFeed.EventDetailsCode.SwingingStrike,
        GameFeed.EventDetailsCode.FoulTip,
        GameFeed.EventDetailsCode.AutomaticBallIntentional
      ] as any).includes(currentEvent.details.code)

      /**
       * PITCHER THROWS BALL
       */
      if (pitchData) {
        setShowBaseball(true)
        const cameraViewOptions: CameraView[] = [
          { type: CameraViewEnum.CatchersPOV },
          { type: CameraViewEnum.AlternateBatterAngle },
          { type: CameraViewEnum.AboveBatter },
          { type: CameraViewEnum.BehindPitcher, pitcher: defense['P'] }
        ]
        const randomCameraView = cameraViewOptions[randInt(0, cameraViewOptions.length - 1)]
        dispatch({ type: 'SET_CAMERA_VIEW', cameraView: randomCameraView })

        const timeToReachStrikezone = calculateTimeToCrossThreshold(pitchData.coordinates.y0, pitchData.coordinates.vY0, pitchData.coordinates.aY, 0)
        const timeToReachCatcher = calculateTimeToCrossThreshold(pitchData.coordinates.y0, pitchData.coordinates.vY0, pitchData.coordinates.aY, -3.3)
        const swingWindupTime = 480 * playSpeed
        const didSwing = (
          currentEvent.details.code === GameFeed.EventDetailsCode.SwingingStrike ||
          currentEvent.details.code === GameFeed.EventDetailsCode.InPlayOut ||
          currentEvent.details.code === GameFeed.EventDetailsCode.InPlayNoOut ||
          currentEvent.details.code === GameFeed.EventDetailsCode.InPlayRun ||
          currentEvent.details.code === GameFeed.EventDetailsCode.Foul ||
          currentEvent.details.code === GameFeed.EventDetailsCode.FoulTip
        )

        trail.reset()

        // baseball
        defense['P'].attachBallToHand(baseball, currentPlay.matchup.pitchHand.code)
        baseball.position.set(0, 0, 0)
        defense['P'].setAnimationState(currentPlay.matchup.pitchHand.code === 'R' ? 'RightHandPitch' : 'LeftHandPitch')
        // animGroup.delay(1300).then(() => )
        await animGroup.delay(PITCH_ANIMATION_DATA.BALL_RELEASE_TIME)
        playSound(throwSound)

        parkScene.add(baseball)

        const ballArcTime = (ballWasNotHit ? timeToReachCatcher : timeToReachStrikezone) ?? 0
        /**  FIELD
         *    +Z
         * -X  Y  +X
         *    -Z
         */

        /**  SPIN DIR
         *    180
         * 90  *  270
         *     0
         */
        const spinClockHand = new Vector3(Math.sin(degToRad(pitchData.breaks.spinDirection + 180)), Math.sin(degToRad(pitchData.breaks.spinDirection + 270)), 0)
        const rotationAxis = spinClockHand.cross({ x: 0, y: 0, z: -1 })

        trail.reset()
        trail.resume()
        trail.activate()

        tone.startTone(400)

        const originalHeight = baseballMesh?.position.y ?? 0
        await Promise.all([
          animGroup.animate((elapsedTime, normalized) => {
            const ballPosition = getBallPositionDuringPitch(pitchData, (elapsedTime / 1000))
            baseball.position.set(ballPosition.x, ballPosition.y, ballPosition.z)
            const slowAmt = 0.5
            // baseballMesh?.setRotationFromAxisAngle(rotationAxis, (-degToRad(elapsedTime * pitchData.breaks.spinRate) / 360) * slowAmt)

            // tone.updateFromFlightProgress(normalized)
            tone.updateFrequency(200 + (ballPosition.y - originalHeight) * 40)
            tone.updateFlangerDepth(.00005)
            tone.updateVolume(normalized * .05)
          }, { playSpeed, duration: ballArcTime * 1000 })
          .then(() => tone.stopTone()),

          // Swing animation
          didSwing && timeToReachStrikezone && (
            animGroup.delay((timeToReachStrikezone * 1000) - swingWindupTime, { playSpeed })
            .then(() => offense['B'].setAnimationState('SwingMid'))
          ),

          timeToReachStrikezone && animGroup.delay(timeToReachStrikezone * 1000, { playSpeed }).then(() => {
            setStrikeZonePosition([feetToM(-pitchData.coordinates.pX), feetToM(pitchData.coordinates.pZ), inchesToM(pitchData.coordinates.pZ)])
          })
        ])

        if (ballWasNotHit) {
          playSound(mitCatchSound)
          defense['C'].setAnimationState('CatcherIdle')
        }

        if (didSwing && !ballWasNotHit) {
          playSound(batHitSound)
          offense['B'].setAnimationState('BattingIdle')
        }

        trail.pause()
        setShowBaseball(false)
      }

      // Handle ball in play animations
      if (currentEvent.details.code === GameFeed.EventDetailsCode.InPlayOut ||
          currentEvent.details.code === GameFeed.EventDetailsCode.InPlayNoOut ||
          currentEvent.details.code === GameFeed.EventDetailsCode.InPlayRun) {
        
        if (ballLandingLocation) {
          setBallDidGround(true)
          setTimeout(() => setBallDidGround(false), 2000)
        }

        // Animate runners
        const runnersToAnimate = currentPlay?.runners
          // include runner movements up to and including this event index
          ?.filter((runner: any) => (runner?.details?.playIndex ?? Number.MAX_SAFE_INTEGER) <= currentEvent.index)
          // ensure start maps to a rendered offensive slot
          ?.filter((runner: any) => (runner?.movement?.start ?? 'B') in offense)

        // DEBUG: Log runner animation details to understand the "running too far on outs" issue
        console.log('[DEBUG] Runner animation analysis:', {
          playOutcome: currentPlay?.result?.description,
          isOut: currentPlay?.result?.isOut,
          eventType: currentPlay?.result?.eventType,
          runnersCount: runnersToAnimate?.length || 0,
          runners: runnersToAnimate?.map((runner: any) => ({
            start: runner?.movement?.start,
            end: runner?.movement?.end,
            outBase: runner?.movement?.outBase,
            isOut: runner?.movement?.isOut,
            playerName: runner?.details?.runner?.fullName
          }))
        })

        if (runnersToAnimate && runnersToAnimate.length > 0) {
          await Promise.all(
            runnersToAnimate.map((runner: any) => {
              const startKey = runner?.movement?.start ?? 'B'
              const endKey = runner?.movement?.end ?? runner?.movement?.outBase ?? 'UNK'
              const idxKey = runner?.details?.playIndex ?? -1
              const movementKey = `${startKey}>${endKey}@${idxKey}`

              // DEBUG: Log each individual runner's animation plan
              console.log('[DEBUG] Animating runner:', {
                playerName: runner?.details?.runner?.fullName,
                startKey,
                endKey,
                isOut: runner?.movement?.isOut,
                outBase: runner?.movement?.outBase,
                movementKey,
                playOutcome: currentPlay?.result?.description
              })

              if (animatedThisEventRef.current.has(movementKey)) {
                return Promise.resolve()
              }

              // @ts-ignore
              const fbx = offense[startKey]
              return animateBaserun({ runner: fbx, movement: runner.movement, playSpeed, animGroup })
                .then(() => {
                  animatedThisEventRef.current.add(movementKey)
                })
                .catch(() => {
                  // Avoid replays within this event even on cancel/error
                  animatedThisEventRef.current.add(movementKey)
                })
            })
          )
        }
      }

      // Mark event as completed for timing synchronization
      console.log('[3D ENGINE] Animation completed, signaling to Statis Pro:', {
        outcome: currentPlay?.result?.description,
        eventIndex,
        isStatisProGame
      })
      dispatch({ type: 'SET_CURRENT_EVENT_COMPLETED' })
      
      // Auto-advance if enabled (for MLB games) - Statis Pro handles its own timing
      if (animationRefs.current.autoPlayEnabled && !isFinalEvent && !isStatisProGame) {
        await animGroup.delay(1000, { playSpeed })
        incrementEvent(1)
      }
    }

    initAnimations().catch(e => {
      if (!(e instanceof CancelledAnimationGroupError)) {
        console.error('Animation error:', e)
      }
    })

    return () => {
      console.log('[3D ENGINE] Cleaning up animation for eventIndex:', eventIndex)
      animGroup.cancelAll()
      
      // Reset visual states
      setShowBaseball(false)
      setStrikeZonePosition(undefined)
      setBallDidGround(false)
      trail.reset()
      trail.pause()
      trail.deactivate()
      
      // Reset player animations to neutral states
      Object.values(offense).forEach(player => {
        if (player.playerPos === 'B') {
          player.setAnimationState('BattingIdle')
        } else {
          player.setAnimationState('StandingNeutral')
        }
      })
      
      Object.values(defense).forEach(player => {
        if (player.playerPos === 'C') {
          player.setAnimationState('CatcherIdle')
        } else {
          player.setAnimationState('StandingNeutral')
        }
      })
      
      // Stop any audio
      tone.stopTone()
    }
  }, [ballLandingLocation, currentEvent, currentPlay, playSpeed, customPlayInfo, eventIndex, offense, defense, dispatch, trail, baseball, parkScene, isFinalEvent, tone, incrementEvent, isFirstEventOfInning])

  return (
    <>
      <directionalLight
        ref={lightRef}
        position={[50, 50, 25]}
        intensity={1}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-far={200}
        shadow-camera-left={-50}
        shadow-camera-right={50}
        shadow-camera-top={50}
        shadow-camera-bottom={-50}
      />
      <object3D ref={lightTargetRef} position={[0, 0, 0]} />
      <ambientLight intensity={0.3} />
      
      <Park scene={parkScene} />
      <Crowd />
      
      {Object.values(offense).map(player => {
        // Only render offensive players that should be on the field
        let shouldRender = false
        
        if (player.playerPos === 'B') {
          // Always render the batter
          shouldRender = true
        } else if (player.playerPos === '1B' && currrentRunnersOnBase['1B']) {
          // Only render 1B runner if there's someone on first base
          shouldRender = true
        } else if (player.playerPos === '2B' && currrentRunnersOnBase['2B']) {
          // Only render 2B runner if there's someone on second base
          shouldRender = true
        } else if (player.playerPos === '3B' && currrentRunnersOnBase['3B']) {
          // Only render 3B runner if there's someone on third base
          shouldRender = true
        }
        
        return shouldRender ? (
          <OffensivePlayer key={player.playerPos} playerControls={player} />
        ) : null
      })}
      
      {Object.values(defense).map(player => (
        <OffensivePlayer key={player.playerPos} playerControls={player} />
      ))}
      
      {showBaseball && (
        <primitive object={baseball} />
      )}
      
      {strikeZonePosition && currentEvent?.pitchData && (
        <StrikeZone pitchData={currentEvent.pitchData} strikeZonePosition={strikeZonePosition} />
      )}
      
      {ballLandingLocation && ballDidHitGround && (
        <BallImpactLocation position={new Vector3(...ballLandingLocation)} />
      )}
      
      {intersectPointDebug && (
        <mesh position={intersectPointDebug}>
          <sphereGeometry args={[0.1]} />
          <meshBasicMaterial color="red" />
        </mesh>
      )}
    </>
  )
}